import {
  LicensingAgenciesService,
  LicensingAgency,
  LicensingAgencyUser,
} from '@/@types/generated'
import Loading from '@/components/Atoms/Loading'
import { hasFeature } from '@/components/Atoms/Permissions'
import { useAuth } from '@/hooks/useAuth'
import { useError } from '@/hooks/useError'
import { LicensingAgentsFeature } from '@/lib/features'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

interface LicensingAgencyContextProps {
  // State
  readonly licensingAgency: LicensingAgency
  readonly licensingAgencyUser: LicensingAgencyUser | null
  readonly isSaving: boolean

  // Actions
  reload: (background?: boolean) => Promise<void>
  approve: () => Promise<void>
  reject: () => Promise<void>
}

const LicensingAgencyContext = createContext<LicensingAgencyContextProps>(
  undefined!
)

export function useLicensingAgency() {
  const context = useContext(LicensingAgencyContext)
  if (context === undefined) {
    throw new Error(
      'useLicensingAgency must be used within a LicensingAgencyProvider route'
    )
  }

  return context
}

export function LicensingAgencyProvider({
  id,
  children,
  onError,
}: React.PropsWithChildren<{
  id: LicensingAgency['id']
  onError?: (error: unknown) => void
}>) {
  // Hooks
  const { showError } = useError()
  const { user: activeUser } = useAuth()

  // State
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [licensingAgency, setLicensingAgency] =
    useState<LicensingAgency | null>(null)
  const [licensingAgencyUser, setLicensingAgencyUser] =
    useState<LicensingAgencyUser | null>(null)

  // Load the licensing agency details
  const reload = useCallback(
    async (background?: boolean) => {
      if (!id) return
      if (!background) setLoading(true)

      try {
        const licensingAgency = await LicensingAgenciesService.get(id)
        setLicensingAgency(licensingAgency)
      } catch (error) {
        onError?.(error)
      } finally {
        setLoading(false)
      }
    },
    [id, onError]
  )

  const loadUser = useCallback(async () => {
    if (!id || !activeUser?.id) return
    if (activeUser.internalUser) return // internal users
    if (!hasFeature(LicensingAgentsFeature.Users, activeUser)) return // non-user managers, ie, LA users

    try {
      const { licensingAgencyUser } = await LicensingAgenciesService.getUser(
        id,
        activeUser.id
      )
      setLicensingAgencyUser(licensingAgencyUser)
    } catch (error) {
      // User may not be on this agency, no error
    }
  }, [activeUser, id])

  // Approve the licensing agency
  const approve = React.useCallback(async () => {
    if (!licensingAgency?.id)
      throw new Error('Cannot approve licensing agency without registration')

    try {
      setSaving(true)

      // Approve the licensing agency
      await LicensingAgenciesService.approve(licensingAgency.id)

      // Reload licensing agency to get updated status
      await reload()
    } catch (error) {
      showError(error, 'Failed to approve licensing agency. Please try again.')
      throw error
    } finally {
      setSaving(false)
    }
  }, [licensingAgency?.id, reload, showError])

  // Reject the licensing agency, after submission and certification
  const reject = React.useCallback(async () => {
    if (!licensingAgency?.id)
      throw new Error('Cannot reject licensing agency without registration')

    try {
      setSaving(true)

      // Reject the licensing agency
      await LicensingAgenciesService.reject(licensingAgency.id)

      // Reload licensing agency to get updated status
      await reload()
    } catch (error) {
      showError(error, 'Failed to reject licensing agency. Please try again.')
      throw error
    } finally {
      setSaving(false)
    }
  }, [licensingAgency?.id, reload, showError])

  // Load licensingAgency details when ID changes
  useEffect(() => {
    reload()
  }, [reload])

  // Load licensing agency user, when it changes
  useEffect(() => {
    loadUser()
  }, [loadUser])

  // No licensingAgency yet, show loading
  if (loading)
    return (
      <Loading
        variant="fullscreen"
        message="Retrieving licensing agency details..."
      />
    )
  if (!licensingAgency) return <div>Licensing agency not found</div>

  return (
    <LicensingAgencyContext.Provider
      value={{
        // State
        licensingAgency,
        licensingAgencyUser,
        isSaving: saving,

        // Actions
        reload,
        approve,
        reject,
      }}
    >
      {children}
    </LicensingAgencyContext.Provider>
  )
}
